import { lazy as reactLazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Wrapper de `React.lazy` que recupera transparentemente o erro clássico
 * pós-deploy: "Failed to fetch dynamically imported module".
 *
 * Cenário: usuário com a tab aberta na release N. Sai o deploy da N+1 —
 * o S3 sync --delete remove os chunks antigos (PoolMatches-CaEvQtDJ.js → 404).
 * Quando o usuário navega pra uma rota lazy, o import() dispara TypeError e
 * cai no ErrorBoundary, gerando issue no Sentry e tela quebrada pro usuário.
 *
 * O listener `vite:preloadError` em main.tsx cobre o caso de chunks pré-carregados
 * via <link rel="modulepreload"> — mas NÃO cobre import() dinâmico vindo do
 * React Router lazy(), que é o caso mais comum. Este wrapper preenche o gap.
 *
 * Estratégia: catch no factory do lazy, detecta a mensagem do erro de chunk,
 * faz reload UMA vez via sessionStorage flag (compartilhada com o listener
 * `vite:preloadError`). Se já recarregou e ainda falhou, deixa o erro
 * propagar normalmente — sinal de que o chunk realmente não existe (não é
 * só deploy antigo).
 *
 * Caso reportado (Sentry 2026-06-06 12:04 BRT, Gabriel Manoel, release
 * 3.0.17): TypeError ao navegar pra /pools/169/matches logo após o deploy
 * de 3.0.18.
 */

const RELOAD_KEY = 'vite-preload-reloaded';
// TTL anti-loop. Se um reload não resolveu em 5 min, deixa o ErrorBoundary
// assumir — provavelmente o chunk realmente sumiu ou tem outro bug. Sem TTL
// o flag ficava preso pra sessão inteira mesmo após resolver.
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

const CHUNK_ERROR_PATTERNS = [
  // Vite / Rollup / browsers
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /error loading dynamically imported module/i,
  // Webpack legacy
  /ChunkLoadError/i,
  // React.lazy quando o módulo veio undefined/parcial (chunk corrompido):
  // Safari mostra: "undefined is not an object (evaluating 'w._result.default')"
  // Chrome mostra: "Cannot read properties of undefined (reading 'default')"
  /\._result\.default/i,
  /Cannot read properties of undefined \(reading 'default'\)/i,
  // Variante named-export do App.tsx: `.then(m => ({ default: m.MyAccount }))`
  // Quando `m` chega undefined: "Cannot read properties of undefined (reading 'MyAccount')"
  // Pattern: undefined + reading 'X' onde X começa com letra MAIÚSCULA (nome de componente).
  /Cannot read properties of undefined \(reading '[A-Z]/,
];

function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as Error)?.message ?? err ?? '');
  const name = String((err as Error)?.name ?? '');
  if (name === 'ChunkLoadError') return true;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg));
}

function shouldAttemptReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0');
    if (!last) return true;
    const elapsed = Date.now() - last;
    // Já recarregou recentemente — não loopa. Deixa ErrorBoundary assumir.
    if (elapsed < RELOAD_COOLDOWN_MS) return false;
    // Passou do cooldown — pode tentar de novo (caso o chunk tenha sumido
    // depois de um deploy diferente).
    return true;
  } catch {
    // sessionStorage indisponível: permite reload, mas pior caso é 1 ciclo
    // — não loop infinito (browser limita reloads em sequência).
    return true;
  }
}

function markReloadAttempt(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage bloqueado — segue sem flag */
  }
}

export function lazy<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return reactLazy(() =>
    factory().catch((err) => {
      if (!isChunkLoadError(err)) throw err;

      // Já tentamos recarregar recentemente — chunk realmente sumiu (ou tem
      // outro bug). Deixa propagar pro ErrorBoundary mostrar a tela de erro.
      if (!shouldAttemptReload()) throw err;
      markReloadAttempt();

      window.location.reload();
      // Reload em curso — Promise nunca resolve, evita race com setState
      // do React lazy num componente prestes a ser desmontado.
      return new Promise<{ default: T }>(() => {});
    }),
  );
}
